import { cvAPI } from 'api/CvAPI'
import { cvParserAPI } from 'api/CvParserAPI'
import { permissionAPI } from 'api/PermissionAPI'
import { personAPI } from 'api/PersonAPI'
import { orderBy } from 'lodash'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { IError } from 'types/error'
import { ICv } from 'types/cvsInterfaces'
import { ProfileState, UseProfileDataParams } from 'types/profileInterfaces'
import { selectPreferredCv } from './profileCvLogic'

export const useProfileData = ({
  personId,
  ready,
  navigate,
  locationState,
  user,
  groups,
  i18n,
  t,
  state,
  setField,
  patch,
  setBackendError,
  setProgressCV,
  setProfileProgress,
}: UseProfileDataParams) => {
  const { personData, selectedCV, refresh, fullCV, updateCvProgress, progressCV } = state
  const latestSectionLoadRef = useRef(0)
  const latestProgressLoadRef = useRef(0)

  const getLocalizedProgress = (
    data: NonNullable<ProfileState['progressCV']>,
    preferredLanguage: string
  ): number | undefined => {
    const byCurrentLanguage = data.progressByLanguage?.[preferredLanguage]
    if (typeof byCurrentLanguage === 'number') {
      return byCurrentLanguage
    }

    for (const language of data.meta?.supportedLanguages ?? []) {
      const value = data.progressByLanguage?.[language]
      if (typeof value === 'number') {
        return value
      }
    }

    const first = Object.values(data.progressByLanguage ?? {}).find(value => typeof value === 'number')
    return first
  }

  const requestedCvId = useMemo(() => {
    if (
      typeof locationState === 'object' &&
      locationState !== null &&
      'search' in locationState &&
      typeof (locationState as { search?: unknown }).search === 'number'
    ) {
      return (locationState as { search: number }).search
    }

    return undefined
  }, [locationState])

  const isCvEmpty = useCallback((): boolean => {
    if (
      personData &&
      selectedCV &&
      ((user && user.Person && selectedCV.personId === user.Person.id) ||
        (groups && groups.includes('sales') && !selectedCV.original))
    ) {
      if (fullCV) {
        return (
          fullCV.Education.length <= 0 &&
          fullCV.Courses.length <= 0 &&
          fullCV.PersonCertificates.length <= 0 &&
          fullCV.PersonCompetences.length <= 0 &&
          (fullCV.LeisureActivities?.length ?? 0) <= 0 &&
          fullCV.Projects.length <= 0 &&
          fullCV.Employers.length <= 0 &&
          fullCV.LanguageSkills.length <= 0 &&
          fullCV.PersonSkills.length <= 0 &&
          fullCV.Industries.length <= 0 &&
          fullCV.Roles.length <= 0 &&
          fullCV.References.length <= 0
        )
      }
    }
    return false
  }, [fullCV, groups, personData, selectedCV, user])

  useEffect(() => {
    if (progressCV) {
      setProfileProgress(getLocalizedProgress(progressCV, i18n.language))
    }
  }, [progressCV, i18n.language, setProfileProgress])

  useEffect(() => {
    const onLanguageChanged = () => {
      if (progressCV) {
        setProfileProgress(getLocalizedProgress(progressCV, i18n.language))
      }
    }

    if (typeof i18n.on === 'function') {
      i18n.on('languageChanged', onLanguageChanged)
    }

    return () => {
      if (typeof i18n.off === 'function') {
        i18n.off('languageChanged', onLanguageChanged)
      }
    }
  }, [i18n, progressCV, setProfileProgress])

  useEffect(() => {
    if (!personData) {
      document.title = 'Caleo | ' + t('loading')
    }
  }, [personData, t])

  useEffect(() => {
    const controller = new AbortController()

    if (personId === null || typeof personId === 'undefined' || Number.isNaN(personId)) {
      if (ready) {
        navigate('/404', { replace: true })
      }
      return
    }

    ;(async () => {
      try {
        setField('personDataLoading', true)
        const data = await personAPI.getPersonData(personId, controller)

        const CVs = orderBy(data.CVs, ['id'], ['asc'])
        data.CVs = CVs
        const originalCv = CVs.find(cv => cv.original === true)
        const nextSelectedCv = selectPreferredCv({
          cvs: CVs,
          requestedCvId,
          defaultCvId: data.defaultCvId ?? undefined,
        })

        patch({
          personData: data,
          original: originalCv,
          selectedCV: nextSelectedCv,
          refresh: false,
        })
      } catch (err) {
        setField('backendError', err as IError)
      } finally {
        setField('personDataLoading', false)
      }
    })()

    return () => {
      controller.abort()
    }
  }, [personId, ready, refresh, navigate, requestedCvId, patch, setField])

  useEffect(() => {
    if (personData && selectedCV && ready && fullCV) {
      document.title = 'Caleo | ' + personData.firstName + ' ' + personData.lastName
      const empty = isCvEmpty()
      const updatedAt = new Date(selectedCV.updatedAt)
      patch({
        ...(state.cvUpdatedAt.getTime() === updatedAt.getTime() ? {} : { cvUpdatedAt: updatedAt }),
        isEmpty: empty,
      })
      if (empty) {
        setField('cvLoading', false)
      }
    }
  }, [personData, selectedCV, ready, fullCV, isCvEmpty, patch, setField, state.cvUpdatedAt])

  useEffect(() => {
    const controller = new AbortController()

    if (selectedCV) {
      const requestId = ++latestSectionLoadRef.current

      ;(async () => {
        try {
          setField('cvLoading', true)
          const initialSectionLoadingState: ProfileState['sectionLoadingState'] = {
            employers: true,
            projects: true,
            education: true,
            leisure_activities: true,
            courses: true,
            certificates: true,
            competences: true,
            language_skills: true,
            references: true,
            urls: true,
            person_skills: true,
            industries: true,
            roles: true,
          }

          // Load progress in background so content sections can render sooner.
          void cvAPI
            .getCvProgress(selectedCV.id, controller)
            .then(cvProgress => {
              if (requestId !== latestSectionLoadRef.current) {
                return
              }

              if (cvProgress) {
                setProgressCV(cvProgress)
              }
            })
            .catch(() => {
              // Ignore progress endpoint errors to avoid blocking profile content.
            })

          // Build CV from selected CV metadata and fill content via section endpoints.
          const cvBase: ICv = {
            ...selectedCV,
            translations: selectedCV.translations ?? [],
            PersonCertificates: selectedCV.PersonCertificates ?? [],
            Courses: selectedCV.Courses ?? [],
            Education: selectedCV.Education ?? [],
            LeisureActivities: selectedCV.LeisureActivities ?? [],
            PersonCompetences: selectedCV.PersonCompetences ?? [],
            Employers: selectedCV.Employers ?? [],
            LanguageSkills: selectedCV.LanguageSkills ?? [],
            PersonSkills: selectedCV.PersonSkills ?? [],
            Industries: selectedCV.Industries ?? [],
            Projects: selectedCV.Projects ?? [],
            References: selectedCV.References ?? [],
            Urls: selectedCV.Urls ?? [],
            Roles: selectedCV.Roles ?? [],
          }
          patch({
            fullCV: cvBase,
            sectionLoadingState: initialSectionLoadingState,
          })

          // Load sections in parallel for faster rendering
          const sections = [
            'employers',
            'projects',
            'education',
            'leisure_activities',
            'courses',
            'certificates',
            'competences',
            'language_skills',
            'references',
            'urls',
            'person_skills',
            'industries',
            'roles',
          ] as const

          // Fetch all sections in parallel and patch each section as it arrives.
          await Promise.allSettled(
            sections.map(async sectionName => {
              try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const sectionData = (await cvAPI.getCvSection(selectedCV.id, sectionName, controller)) as any

                if (requestId !== latestSectionLoadRef.current) {
                  return
                }

                switch (sectionName) {
                  case 'employers':
                    cvBase.Employers = sectionData
                    break
                  case 'projects':
                    cvBase.Projects = sectionData
                    break
                  case 'education':
                    cvBase.Education = sectionData
                    break
                  case 'leisure_activities':
                    cvBase.LeisureActivities = sectionData
                    break
                  case 'courses':
                    cvBase.Courses = sectionData
                    break
                  case 'certificates':
                    cvBase.PersonCertificates = sectionData
                    break
                  case 'competences':
                    cvBase.PersonCompetences = sectionData
                    break
                  case 'language_skills':
                    cvBase.LanguageSkills = sectionData
                    break
                  case 'references':
                    cvBase.References = sectionData
                    break
                  case 'urls':
                    cvBase.Urls = sectionData
                    break
                  case 'person_skills':
                    cvBase.PersonSkills = sectionData
                    break
                  case 'industries':
                    cvBase.Industries = sectionData
                    break
                  case 'roles':
                    cvBase.Roles = sectionData
                    break
                }

                patch({ fullCV: { ...cvBase } })
              } finally {
                if (requestId === latestSectionLoadRef.current) {
                  setField('sectionLoadingState', prev =>
                    prev[sectionName] ? { ...prev, [sectionName]: false } : prev
                  )
                }
              }
            })
          )
        } catch (err) {
          setField('backendError', err as IError)
        } finally {
          setField('cvLoading', false)
        }
      })()
    }

    return () => {
      controller.abort()
    }
  }, [selectedCV, patch, setField, setProgressCV])

  useEffect(() => {
    const controller = new AbortController()

    if (selectedCV && updateCvProgress) {
      const requestId = ++latestProgressLoadRef.current

      ;(async () => {
        try {
          const data = await cvAPI.getCvProgress(selectedCV.id, controller)

          if (requestId !== latestProgressLoadRef.current) {
            return
          }

          setProgressCV(data)
        } catch (err) {
          setBackendError(err as IError)
        }
      })()
    }

    return () => {
      controller.abort()
    }
  }, [selectedCV, updateCvProgress, setBackendError, setProgressCV])

  useEffect(() => {
    const controller = new AbortController()

    if (personData) {
      ;(async () => {
        try {
          setField('teamAccessLoading', true)
          const [personTeamAccessData, parseCreditsData] = await Promise.all([
            permissionAPI.getPersonTeamAccess(personData.id, controller),
            personData.organizationId
              ? cvParserAPI.getParseCredits(personData.organizationId, controller)
              : Promise.resolve(0),
          ])

          patch({
            personTeamAccess: personTeamAccessData,
            parseCredits: parseCreditsData,
          })
        } catch (err) {
          setField('backendError', err as IError)
        } finally {
          setField('teamAccessLoading', false)
        }
      })()
    }

    return () => {
      controller.abort()
    }
  }, [personData, patch, setField])
}
